import { useMemo, useState } from "react";
import { Box, Typography } from "@mui/material";
import { ResponsiveContainer, Sankey, Tooltip as RTooltip } from "recharts";
import { useReportData } from "./UseReportData";
import { ChartCard, Empty, Seg, SectionFrame, SimpleTable, StatusDot, StatusKey, fmt, fmtPct, fmtT, hexA, statusOf, useChartColors, varPct } from "./Reportkit";

function SankeyNode({ x, y, width, height, payload, containerWidth, textColor }) {
  const right = x + width + 90 > containerWidth;
  return (
    <g>
      <rect x={x} y={y} width={width} height={height} fill={payload.color} fillOpacity={0.9} />
      <text x={right ? x - 6 : x + width + 6} y={y + height / 2} textAnchor={right ? "end" : "start"} dominantBaseline="middle" fontSize={11} fontWeight={600} fill={textColor}>{payload.name}</text>
    </g>
  );
}

function Flow({ routes }) {
  const c = useChartColors();
  const [m, setM] = useState("actual");
  const data = useMemo(() => {
    const names = [], idx = new Map(), colors = [];
    const node = (name, color) => { if (!idx.has(name)) { idx.set(name, names.length); names.push({ name, color }); } return idx.get(name); };
    const links = new Map();
    const add = (a, b, v) => { const k = `${a}>${b}`; links.set(k, (links.get(k) || 0) + v); };
    routes.forEach((r) => {
      const v = r[m]; if (!(v > 0)) return;
      const s = node(r.loadLocation, c.series[5]), mt = node(r.material, c.series[r.cls === "Ore" ? 3 : 2]), dd = node(r.dumpLocation, c.brand);
      add(`${s}`, `${mt}`, v); add(`${mt}`, `${dd}`, v);
    });
    return { nodes: names, links: [...links.entries()].map(([k, value]) => { const [source, target] = k.split(">").map(Number); return { source, target, value }; }) };
  }, [routes, m, c]);
  return (
    <ChartCard title="Material flow — source → material → destination" sub="Band width = tonnes" action={<Seg value={m} onChange={setM} options={[["actual", "Actual"], ["plan", "Plan"]]} />}>
      {data.links.length === 0 ? <Empty /> : (
        <ResponsiveContainer width="100%" height={Math.max(320, data.nodes.length * 22)}>
          <Sankey data={data} nodePadding={18} nodeWidth={12} margin={{ top: 8, right: 110, bottom: 8, left: 90 }} link={{ stroke: c.series[0], strokeOpacity: 0.25 }}
            node={(p) => <SankeyNode {...p} textColor={c.text} />}>
            <RTooltip formatter={(v) => fmtT(v)} />
          </Sankey>
        </ResponsiveContainer>
      )}
    </ChartCard>
  );
}

const METRICS = {
  actual: ["Actual tonnes", (a) => a.actual, fmtT], plan: ["Plan tonnes", (a) => a.plan, fmtT], var: ["Variance %", (a) => varPct(a.actual, a.plan), (v) => fmtPct(v, 1, true)],
  trips: ["Trips", (a) => a.trips, fmt], cycle: ["Cycle min / trip", (a) => (a.trips ? a.cycleMin / a.trips : null), (v) => fmt(v, 1)], km: ["Loaded haul km", (a) => (a.trips ? a.haulKm / a.trips : null), (v) => fmt(v, 2)],
};

function RouteHeat({ routes }) {
  const c = useChartColors();
  const [metric, setMetric] = useState("actual");
  const { rows, cols, cell } = useMemo(() => {
    const cell = new Map(), R = new Set(), C = new Set();
    routes.forEach((r) => {
      const k = `${r.loadLocation}|${r.dumpLocation}`; R.add(r.loadLocation); C.add(r.dumpLocation);
      const o = cell.get(k) || { actual: 0, plan: 0, trips: 0, cycleMin: 0, haulKm: 0 };
      o.actual += r.actual; o.plan += r.plan; o.trips += r.trips; o.cycleMin += r.cycleMin; o.haulKm += r.haulKm; cell.set(k, o);
    });
    return { rows: [...R].sort(), cols: [...C].sort(), cell };
  }, [routes]);
  const [label, val, f] = METRICS[metric];
  const vals = [...cell.values()].map(val).filter((v) => v != null && Number.isFinite(v));
  const mx = Math.max(1e-9, ...vals.map(Math.abs));
  return (
    <ChartCard title="Load → dump route heat map" sub="Rows = load location, columns = destination" action={
      <Seg value={metric} onChange={setMetric} options={Object.entries(METRICS).map(([k, v]) => [k, v[0].replace(" tonnes", "").replace(" min / trip", " cycle")])} />}>
      <Box sx={{ overflow: "auto" }}>
        <Box sx={{ display: "grid", gridTemplateColumns: `130px repeat(${cols.length}, minmax(96px, 1fr))`, gap: "3px", minWidth: "max-content" }}>
          <Box />
          {cols.map((x) => <Typography key={x} variant="caption" sx={{ fontWeight: 600, textAlign: "center" }}>{x}</Typography>)}
          {rows.map((r) => (
            <Box key={r} sx={{ display: "contents" }}>
              <Typography variant="caption" sx={{ fontWeight: 600, alignSelf: "center" }}>{r}</Typography>
              {cols.map((x) => {
                const a = cell.get(`${r}|${x}`);
                if (!a) return <Box key={x} sx={{ borderRadius: 1, bgcolor: "action.hover", minHeight: 44 }} />;
                const v = val(a);
                const st = statusOf(a.actual, a.plan);
                const bg = metric === "var" ? hexA(st === "none" ? "#9CA3AF" : { good: "#0CA30C", warning: "#FAB219", critical: "#D03B3B" }[st], 0.2 + Math.min(1, Math.abs(v || 0) / 15) * 0.5) : hexA(c.series[0], 0.12 + (Math.abs(v || 0) / mx) * 0.75);
                return (
                  <Box key={x} title={`${r} → ${x}\nActual ${fmtT(a.actual)} · Plan ${fmtT(a.plan)} · ${fmtPct(varPct(a.actual, a.plan), 1, true)}`}
                    sx={{ borderRadius: 1, p: 0.75, textAlign: "center", bgcolor: bg, minHeight: 44 }}>
                    <Typography variant="caption" fontWeight={700} component="div">{f(v)}</Typography>
                    <Typography sx={{ fontSize: 10 }} color="text.secondary" component="div">{a.plan ? <StatusDot status={st} size={11} /> : null} {fmtPct(varPct(a.actual, a.plan), 0, true)}</Typography>
                  </Box>
                );
              })}
            </Box>
          ))}
        </Box>
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>Metric: {label}. Empty = no material on that route.</Typography>
    </ChartCard>
  );
}

export default function FlowSection({ req }) {
  const st = useReportData("production", req);
  const d = st.data;
  const tableRows = useMemo(() => (d ? [...d.routes].sort((a, b) => b.actual - a.actual) : []), [d]);
  return (
    <SectionFrame eyebrow="Where material moves" title="Material Flow & Routes" desc="Sources, materials and destinations — plan vs actual per route" state={st}>
      {d && (
        <>
          <Flow routes={d.routes} />
          <RouteHeat routes={d.routes} />
          <ChartCard title="Every route" sub="Load location → material → destination">
            <SimpleTable max={460} cols={[
              { key: "loadLocation", label: "Load", align: "left", render: (r) => `${r.loadLocation} (${r.bench})` }, { key: "material", label: "Material", align: "left" },
              { key: "dumpLocation", label: "Destination", align: "left", render: (r) => `${r.dumpLocation} · ${r.dumpArea}` }, { key: "loader", label: "Loader", align: "left" },
              { key: "plan", label: "Plan", render: (r) => fmtT(r.plan) }, { key: "actual", label: "Actual", render: (r) => <b>{fmtT(r.actual)}</b> },
              { key: "v", label: "Var %", render: (r) => <>{fmtPct(varPct(r.actual, r.plan), 1, true)} <StatusDot status={statusOf(r.actual, r.plan)} size={12} /></> },
              { key: "trips", label: "Trips", render: (r) => fmt(r.trips) }, { key: "cyc", label: "Cycle min", render: (r) => fmt(r.trips ? r.cycleMin / r.trips : null, 1) },
              { key: "km", label: "Haul km", render: (r) => fmt(r.trips ? r.haulKm / r.trips : null, 2) },
            ]} rows={tableRows} />
            <Box sx={{ mt: 1 }}><StatusKey /></Box>
          </ChartCard>
        </>
      )}
    </SectionFrame>
  );
}